import React, { useState } from 'react';
import { useCart, CartItem } from '../contexts/CartContext';
import OnlineCheckout from './OnlineCheckout';
import './ShoppingCart.css';

interface ShoppingCartProps {
  onCheckout?: () => void;
}

const ShoppingCart: React.FC<ShoppingCartProps> = () => {
  const [showCheckout, setShowCheckout] = useState(false);
  const {
    items,
    store,
    itemCount,
    subtotal,
    removeItem,
    updateQuantity,
    updateSpecialInstructions,
    clearCart,
    isCartOpen,
    closeCart
  } = useCart();

  const [editingInstructions, setEditingInstructions] = useState<string | null>(null);
  const [instructionsText, setInstructionsText] = useState('');

  const TAX_RATE = 0.07; // 7% tax - this should come from store config
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  const handleQuantityChange = (menuItemId: string, delta: number, currentQty: number) => {
    const newQty = currentQty + delta;
    if (newQty <= 0) {
      removeItem(menuItemId);
    } else {
      updateQuantity(menuItemId, newQty);
    }
  };

  const startEditingInstructions = (item: CartItem) => {
    setEditingInstructions(item.menuItemId);
    setInstructionsText(item.specialInstructions || '');
  };

  const saveInstructions = (menuItemId: string) => {
    updateSpecialInstructions(menuItemId, instructionsText);
    setEditingInstructions(null);
    setInstructionsText('');
  };

  const cancelEditingInstructions = () => {
    setEditingInstructions(null);
    setInstructionsText('');
  };

  if (!isCartOpen) {
    return null;
  }

  return (
    <div className="cart-overlay" onClick={closeCart}>
      <div className="cart-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cart-header">
          <h2>Your Order</h2>
          <button className="cart-close-btn" onClick={closeCart}>
            &times;
          </button>
        </div>

        {/* Store Info */}
        {store && (
          <div className="cart-store-info">
            <span className="store-name">{store.storeName}</span>
          </div>
        )}

        {/* Empty Cart */}
        {items.length === 0 ? (
          <div className="cart-empty">
            <div className="empty-cart-icon">
              <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            </div>
            <p>Your cart is empty</p>
            <p className="empty-cart-hint">Add items from the menu to get started</p>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="cart-items">
              {items.map((item) => (
                <div key={item.menuItemId} className="cart-item">
                  <div className="cart-item-main">
                    {item.imageUrl && (
                      <div className="cart-item-image">
                        <img src={item.imageUrl} alt={item.name} />
                      </div>
                    )}
                    <div className="cart-item-details">
                      <h3 className="cart-item-name">{item.name}</h3>
                      <p className="cart-item-price">${item.price.toFixed(2)} each</p>

                      {/* Special Instructions */}
                      {editingInstructions === item.menuItemId ? (
                        <div className="instructions-edit">
                          <textarea
                            value={instructionsText}
                            onChange={(e) => setInstructionsText(e.target.value)}
                            placeholder="Add special instructions..."
                            rows={2}
                          />
                          <div className="instructions-buttons">
                            <button
                              className="instructions-save"
                              onClick={() => saveInstructions(item.menuItemId)}
                            >
                              Save
                            </button>
                            <button
                              className="instructions-cancel"
                              onClick={cancelEditingInstructions}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="add-instructions-btn"
                          onClick={() => startEditingInstructions(item)}
                        >
                          {item.specialInstructions ? 'Edit instructions' : '+ Add instructions'}
                        </button>
                      )}

                      {item.specialInstructions && editingInstructions !== item.menuItemId && (
                        <p className="cart-item-instructions">{item.specialInstructions}</p>
                      )}
                    </div>

                    <div className="cart-item-actions">
                      <div className="quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() => handleQuantityChange(item.menuItemId, -1, item.quantity)}
                        >
                          -
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => handleQuantityChange(item.menuItemId, 1, item.quantity)}
                        >
                          +
                        </button>
                      </div>
                      <p className="cart-item-subtotal">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <button
                        className="remove-item-btn"
                        onClick={() => removeItem(item.menuItemId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="cart-summary">
              <div className="summary-row">
                <span>Subtotal ({itemCount} items)</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Tax</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="cart-actions">
              <button className="clear-cart-btn" onClick={clearCart}>
                Clear Cart
              </button>
              <button
                className="checkout-btn"
                onClick={() => setShowCheckout(true)}
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </div>

      {/* Online Checkout Modal */}
      {showCheckout && (
        <OnlineCheckout
          onClose={() => {
            setShowCheckout(false);
            closeCart();
          }}
          onOrderComplete={(orderId) => {
            console.log('Order completed:', orderId);
          }}
        />
      )}
    </div>
  );
};

export default ShoppingCart;
